/** Query key registry for Feature 5.3 analytics dashboards (Controller persona, reports #20-#28). */

const controllerRoot = ['analytics', 'controller'] as const;

export const analyticsControllerQueryKeys = {
  all: controllerRoot,
  testSummary: () => [...controllerRoot, 'test-summary'] as const,
  failingControls: () => [...controllerRoot, 'failing-controls'] as const,
  exceptionRate: () => [...controllerRoot, 'exception-rate'] as const,
  sampleSize: () => [...controllerRoot, 'sample-size'] as const,
  rollforward: () => [...controllerRoot, 'rollforward'] as const,
  ipeStatus: () => [...controllerRoot, 'ipe-status'] as const,
  todToe: () => [...controllerRoot, 'tod-toe'] as const,
  sodVerified: () => [...controllerRoot, 'sod-verified'] as const,
  evidenceCoverage: () => [...controllerRoot, 'evidence-coverage'] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (CAE/Manager persona, reports #9-#19). */
const caeRoot = ['analytics', 'cae'] as const;

export const analyticsCaeQueryKeys = {
  all: caeRoot,
  controlUniverse: () => [...caeRoot, 'control-universe'] as const,
  testingStatus: () => [...caeRoot, 'testing-status'] as const,
  issuesPipeline: () => [...caeRoot, 'issues-pipeline'] as const,
  coverageMatrix: () => [...caeRoot, 'coverage-matrix'] as const,
  mrc: () => [...caeRoot, 'mrc'] as const,
  exceptionsSummary: () => [...caeRoot, 'exceptions-summary'] as const,
  auditorReliance: () => [...caeRoot, 'auditor-reliance'] as const,
  actionPlanAging: () => [...caeRoot, 'action-plan-aging'] as const,
  controlsByDept: () => [...caeRoot, 'controls-by-dept'] as const,
  itgcCoverage: () => [...caeRoot, 'itgc-coverage'] as const,
  testerWorkload: () => [...caeRoot, 'tester-workload'] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (CFO/Executive persona, reports #1-#8). */
const cfoRoot = ['analytics', 'cfo'] as const;

export const analyticsCfoQueryKeys = {
  all: cfoRoot,
  complianceScore: () => [...cfoRoot, 'compliance-score'] as const,
  coverageHeatmap: () => [...cfoRoot, 'coverage-heatmap'] as const,
  openIssues: () => [...cfoRoot, 'open-issues'] as const,
  programProgress: () => [...cfoRoot, 'program-progress'] as const,
  socCoverage: () => [...cfoRoot, 'soc-coverage'] as const,
  riskLandscape: () => [...cfoRoot, 'risk-landscape'] as const,
  repeatEscalated: () => [...cfoRoot, 'repeat-escalated'] as const,
  scoreTrend: () => [...cfoRoot, 'score-trend'] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (Tester "my work" persona, reports #30-#36 + feedback round 2026-08). */
const testerRoot = ['analytics', 'tester'] as const;

export const analyticsTesterQueryKeys = {
  all: testerRoot,
  myControls: (assigneeUserId?: number | null) => [...testerRoot, 'my-controls', assigneeUserId ?? null] as const,
  dueThisWeek: (assigneeUserId?: number | null) => [...testerRoot, 'due-this-week', assigneeUserId ?? null] as const,
  myProgress: (assigneeUserId?: number | null) => [...testerRoot, 'my-progress', assigneeUserId ?? null] as const,
  myExceptions: (assigneeUserId?: number | null) => [...testerRoot, 'my-exceptions', assigneeUserId ?? null] as const,
  evidenceChecklist: (runId: string | undefined) => [...testerRoot, 'evidence-checklist', runId ?? null] as const,
  rerunsRequired: (assigneeUserId?: number | null) => [...testerRoot, 'reruns-required', assigneeUserId ?? null] as const,
  avgTestTime: () => [...testerRoot, 'avg-test-time'] as const,
  testers: () => [...testerRoot, 'testers'] as const,
} as const;

/** Query key registry for Feature 5.3 analytics dashboards (External Auditor persona, reports #37-#41 + feedback round 2026-08). */
const auditorRoot = ['analytics', 'auditor'] as const;

export const analyticsAuditorQueryKeys = {
  all: auditorRoot,
  controlInventory: () => [...auditorRoot, 'control-inventory'] as const,
  workpapersPackage: (controlId: string | undefined) => [...auditorRoot, 'workpapers-package', controlId ?? null] as const,
  exceptionReport: () => [...auditorRoot, 'exception-report'] as const,
  issueLedger: () => [...auditorRoot, 'issue-ledger'] as const,
  relianceEligible: () => [...auditorRoot, 'reliance-eligible'] as const,
  processChartingStatus: () => [...auditorRoot, 'process-charting-status'] as const,
} as const;

/**
 * Query key registry for the new `/api/analytics/scope/*` endpoints
 * (feedback round 2026-08) — shared cross-persona scope/coverage data (CFO,
 * CAE, Controller, and Auditor dashboards all pull from these same keys).
 */
const scopeRoot = ['analytics', 'scope'] as const;

export const analyticsScopeQueryKeys = {
  all: scopeRoot,
  controlCounts: () => [...scopeRoot, 'control-counts'] as const,
  riskSummary: () => [...scopeRoot, 'risk-summary'] as const,
  systems: () => [...scopeRoot, 'systems-in-scope'] as const,
  entities: () => [...scopeRoot, 'entities-in-scope'] as const,
  materiality: () => [...scopeRoot, 'materiality'] as const,
  plannedCompleted: () => [...scopeRoot, 'planned-vs-completed'] as const,
  overdueSummary: () => [...scopeRoot, 'overdue-summary'] as const,
  testingStatusRollup: () => [...scopeRoot, 'testing-status-rollup'] as const,
} as const;

/** Query key registry for the new Reviewer persona (dashboard_key 'reviewer', feedback round 2026-08). */
const reviewerRoot = ['analytics', 'reviewer'] as const;

export const analyticsReviewerQueryKeys = {
  all: reviewerRoot,
  queue: () => [...reviewerRoot, 'queue'] as const,
  summary: () => [...reviewerRoot, 'summary'] as const,
} as const;
